/**
 * The real `GameAudio`: wires the desk, the bank, the voice pool, the loops and the score
 * together behind the interface in `../index.ts`. Everything that knows about WebAudio lives
 * under `engine/`; everything it decides with is pure and lives one level up.
 */

import type { MissionDef, SimEvent, World } from '../../sim/types';
import type { GameAudio, UiSound } from '../index';
import { TICK_MS } from '../music/schedule';
import { Occlusion } from '../occlusion';
import { type AudioSettings, DEFAULT_SETTINGS, mergeSettings } from '../settings';
import { Thunder, type ThunderSky } from '../thunder';
import type { SfxId } from '../sfx';
import { createSurfaceLookup } from '../surface';
import { Bank } from './bank';
import { EventSounds } from './events';
import { Graph } from './graph';
import { Loops } from './loops';
import { Pulses } from './pulses';
import { Score } from './score';
import { Steps } from './steps';
import { Voices } from './voices';

const UI_SFX: Partial<Record<UiSound, SfxId>> = {
  move: 'uiMove', confirm: 'uiConfirm', back: 'uiBack', stamp: 'uiStamp', type: 'uiType',
};
/** Milliseconds per heartbeat spent rendering the bank while it is still cold. */
const WARM_BUDGET_MS = 4;
/** No `update` for this long while in a mission = paused or hidden: the loops go quiet. */
const STALL_SECONDS = 0.3;

export class LiveAudio implements GameAudio {
  private readonly graph: Graph;
  private readonly bank: Bank;
  private readonly voices: Voices;
  private readonly score: Score;
  private readonly steps: Steps;
  private readonly pulses: Pulses;
  private readonly thunder = new Thunder();
  /** The sky this mission is under: what `Thunder` watches for a stroke. */
  private sky: ThunderSky | null = null;
  /** The continuous voices hold a dozen running oscillators: not built until a mission needs them. */
  private loops: Loops | null = null;
  private events: EventSounds | null = null;
  private current: AudioSettings = { ...DEFAULT_SETTINGS };
  private readonly timer: ReturnType<typeof setInterval>;
  private warm = false;
  private inMission = false;
  private lastUpdate = 0;
  private stalled = false;

  constructor(Ctor: typeof AudioContext) {
    this.graph = new Graph(Ctor);
    this.bank = new Bank(this.graph.ctx);
    this.voices = new Voices(this.graph, this.bank);
    this.score = new Score(this.graph, this.bank);
    this.steps = new Steps(this.voices);
    this.pulses = new Pulses(this.voices);
    this.graph.apply(this.current);
    this.timer = setInterval(() => this.heartbeat(), TICK_MS);
  }

  /** Off the render loop, so music plays in menus and survives a slow frame. */
  private heartbeat(): void {
    this.score.tick();
    if (!this.warm) this.warm = this.bank.warm(WARM_BUDGET_MS);
    if (this.inMission && !this.stalled && this.graph.now - this.lastUpdate > STALL_SECONDS) {
      this.stalled = true;
      this.loops?.quiet();
    }
  }

  resume(): void {
    this.graph.resume();
  }

  settings(): AudioSettings {
    return { ...this.current };
  }

  applySettings(next: Partial<AudioSettings>): void {
    this.current = mergeSettings(this.current, next);
    this.graph.apply(this.current);
  }

  playTitleTheme(): void {
    this.score.playTitle();
  }

  stopMusic(): void {
    this.score.stop();
  }

  uiClick(kind: UiSound): void {
    const id = UI_SFX[kind];
    if (id) this.voices.playUi(id);
    else if (kind === 'medal' || kind === 'promotion' || kind === 'memorial') this.score.cue(kind);
  }

  beginMission(def: MissionDef): void {
    this.loops ??= new Loops(this.graph);
    this.events ??= new EventSounds(this.graph, this.voices, this.score, this.loops);
    const occlusion = new Occlusion(def);
    this.voices.occlusion = occlusion;
    this.loops.occlusion = occlusion;
    this.events.occlusion = occlusion;
    this.graph.setMuffle(def.biome === 'arctic');
    this.loops.begin(def);
    this.events.begin(def);
    this.steps.begin(createSurfaceLookup(def));
    this.pulses.begin();
    this.score.beginMission(def);
    // A thunderstorm throws lightning, and thunder follows it (02 §5, `thunder.ts`).
    this.thunder.reset();
    this.sky = { sky: def, seed: def.seed };
    this.inMission = true;
    this.stalled = false;
    this.lastUpdate = this.graph.now;
  }

  handle(events: readonly SimEvent[], world: World, listenerX: number, listenerZ: number): void {
    if (!this.inMission || events.length === 0) return;
    this.voices.setListener(listenerX, listenerZ);
    this.events?.handle(events, world, listenerX, listenerZ);
  }

  update(world: World, dt: number, listenerX: number, listenerZ: number): void {
    if (!this.inMission) return;
    // dt = 0 is a pause: the music carries on (off its own timer), but the world must not drone on
    // under the pause menu - leave the stall watchdog to quiet the loops.
    if (dt <= 0) return;
    this.lastUpdate = this.graph.now;
    this.stalled = false;
    this.voices.setListener(listenerX, listenerZ);
    this.loops?.update(world, dt, listenerX, listenerZ);
    this.steps.update(world, listenerX, listenerZ);
    this.pulses.update(world, dt);
    // Overhead everywhere, so it goes on the UI bus unpanned rather than at a point on the map.
    if (this.sky) this.thunder.step(this.sky, world.tick, dt, (gain: number) => this.voices.playUi('thunder', gain));
    this.score.update(world, this.current.score1987);
    this.score.tick();
  }

  endMission(): void {
    if (!this.inMission) return;
    this.inMission = false;
    this.loops?.end();
    this.steps.end();
    this.voices.occlusion = null;
    this.graph.setMuffle(false);
    this.score.endMission();
    this.thunder.reset();
    this.sky = null;
  }

  dispose(): void {
    clearInterval(this.timer);
    this.voices.stopAll();
    this.graph.dispose();
  }
}
